import React, { useContext, useEffect } from "react";
import {
  ChatContext,
  IOrganizationContext,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import EmptyRecordView from "../../common/EmptyRecordView";
import {
  FEATURES,
  IAdvanceSearchFilter,
  IBroadcastInfoMembers,
  IGroupInfoMembers,
  IUserInfo,
} from "@home-health-notify/domain";
import { SearchAndFilterUsers } from "./SearchAndFilterUsers";
import { AddUserListContainer } from "../AddMember.style";
import AddUserListItem from "./AddUserListItem";
import AdvanceSearch from "./advanceSearch/AdvanceSearch";
import ProgressLoader from "../../loader/ProgressLoader";
import { ScrollableList as UserList } from "../../common/scrollableList/ScrollableList";
import { IPatientHeaderProps } from "./advanceSearch/AdvanceSearchHelper";

export type AddUserListType = {
  existingGroup?: boolean;
  existingMembers?: IGroupInfoMembers[] | IBroadcastInfoMembers[];
  tabValue?: number;
  enableReferrals?: boolean;
  advanceSearchFilters?: IAdvanceSearchFilter;
  users: IUserInfo[];
  hideSearchAndFilters?: boolean;
  onScrollCallback?: () => void;
  isLoading?: boolean;
  patientHeaderProps?: IPatientHeaderProps | undefined;
  onAddMemberCallback: (selectedMembers: any) => void;
  showActions?: boolean;
  onItemClick?: (userId: string) => void;
  selectedUserId?: string | null;
};

const AddUserList = ({
  existingMembers,
  tabValue = 0,
  enableReferrals = false,
  existingGroup = false,
  advanceSearchFilters,
  users,
  hideSearchAndFilters,
  onScrollCallback,
  isLoading = false,
  patientHeaderProps,
  onAddMemberCallback,
  showActions = true,
  onItemClick,
  selectedUserId,
}: AddUserListType) => {
  const { chat } = useContext(ChatContext);
  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);

  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "advanceSearch" });

  const hasAccessAdvanceSearch = selectedOrgUser?.hasAccess(
    FEATURES.ADVANCE_SEARCH,
  );

  const [finalUserDirectory, setFinalUserDirectory] =
    React.useState<IUserInfo[]>(users);

  const handleFinalDirectoryChange = (filteredDirectory: IUserInfo[]) => {
    setFinalUserDirectory(filteredDirectory);
  };

  const onResetUserDirectory = () => {
    setFinalUserDirectory(users);
  };

  useEffect(() => {
    hideSearchAndFilters && setFinalUserDirectory(users);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  return (
    <AddUserListContainer data-testid="add-user-list-container">
      {!hideSearchAndFilters && (
        <>
          <SearchAndFilterUsers
            tabValue={tabValue}
            onFinalUserDirectoryChange={handleFinalDirectoryChange}
            onResetUserDirectory={onResetUserDirectory}
            userDirectory={users}
          />
          {hasAccessAdvanceSearch && (
            <AdvanceSearch
              advanceSearchFilters={advanceSearchFilters}
              existingMembers={existingMembers as IGroupInfoMembers[]}
              existingGroup={existingGroup}
              patientHeaderProps={patientHeaderProps}
              onAddMemberCallback={onAddMemberCallback}
              openedFrom="add_members_flow"
            />
          )}
        </>
      )}
      {finalUserDirectory.length > 0 ? (
        <UserList
          data-testid="add-user-list"
          totalCount={finalUserDirectory.length}
          onScrollCallback={onScrollCallback}
          data={finalUserDirectory}
          itemContent={(index, userInfo: IUserInfo) => (
            <AddUserListItem
              key={userInfo.id + index}
              chat={chat}
              userInfo={userInfo}
              existingMember={existingMembers}
              enableReferrals={enableReferrals}
              existingGroup={existingGroup}
              showWorkloadDetails={hideSearchAndFilters}
              showActions={showActions}
              onItemClick={onItemClick}
              isSelected={selectedUserId === userInfo.id}
            />
          )}
        />
      ) : isLoading ? (
        <ProgressLoader
          label={tNs("loading")}
          style={{
            height: "auto",
          }}
        />
      ) : (
        <EmptyRecordView emptyMessage={t("emptySearchRecordMessage")} />
      )}
    </AddUserListContainer>
  );
};
export default AddUserList;
